/**
 * issuesWorkColumns — Spalten der Optimierungen-Arbeitsliste (P2a/R0).
 * Reuse: name/Sales-Spalten kommen aus createMergedColumns (keine zweite
 * Definition); NEU sind nur `violations` (IssueChip-Details) und die
 * Beheben-Aktion. quantity_90d/revenue_90d sind über den ColumnPicker
 * zuschaltbar (Default hidden via Task 3 columnVisibility).
 */
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'
import type { TFunction } from 'i18next'

import { Button } from '@/components/v2/ui'
import { IssueChip } from '@/components/v2/products/issues/IssueChip'
import { createMergedColumns } from '@/components/v2/products/mergedProductColumns'
import type { ProductWithReadiness } from '@/types/products'

import type { IssuesWorkRow } from './workTableAdapter'

const col = createColumnHelper<ProductWithReadiness>()
const REUSED = ['name', 'quantity_30d', 'revenue_30d', 'quantity_90d', 'revenue_90d']

const colId = (c: { id?: string; accessorKey?: string }) => c.id ?? c.accessorKey ?? ''

function violationsColumn(t: TFunction) {
  return col.display({
    id: 'violations',
    header: t('productsIssues.workTable.violations'),
    cell: ({ row }) => {
      const issues = (row.original as IssuesWorkRow).work_issues ?? []
      const shown = issues.slice(0, 2)
      return (
        <div className="flex flex-wrap items-center gap-1">
          {shown.map((i, idx) => (
            <IssueChip key={idx} issue={i} />
          ))}
          {issues.length > 2 && (
            <span className="text-xs text-neutral-500">+{issues.length - 2}</span>
          )}
        </div>
      )
    },
  })
}

function fixColumn(t: TFunction, onFix: (row: IssuesWorkRow) => void) {
  return col.display({
    id: 'work_actions',
    header: '',
    cell: ({ row }) => (
      <Button
        size="sm"
        variant="ghost"
        data-testid="work-row-fix-btn"
        onClick={(e) => {
          e.stopPropagation()
          onFix(row.original as IssuesWorkRow)
        }}
      >
        {t('productsIssues.workList.fix')}
      </Button>
    ),
  })
}

export function createIssuesWorkColumns(
  t: TFunction,
  onFix: (row: IssuesWorkRow) => void,
): ColumnDef<ProductWithReadiness>[] {
  const noop = () => undefined
  const reused = createMergedColumns(t, noop, noop).filter((c) => REUSED.includes(colId(c)))
  const byId = (id: string) => reused.find((c) => colId(c) === id)
  return [
    byId('name'),
    violationsColumn(t),
    byId('quantity_30d'),
    byId('revenue_30d'),
    byId('quantity_90d'),
    byId('revenue_90d'),
    fixColumn(t, onFix),
  ].filter(Boolean) as ColumnDef<ProductWithReadiness>[]
}
